import React, { useContext, useEffect, useRef, useState } from "react";
import { SlideDown } from "react-slidedown";
import "react-slidedown/lib/slidedown.css";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { isMobile } from "react-device-detect";
import ReCAPTCHA from "react-google-recaptcha";

import { AvatarInput, Alert, Loading } from "../../components";
import {
  BasicInformation,
  SecondaryInformation,
  ProfileEditView,
  VerificationCard,
} from "./index.jsx";
import A2SHPopup from "../../components/A2HSPopup";
import { SettingsContext } from "../../contexts";
import { verificationConstant } from "../../constants";

const RECAPTCHA_SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY;

const Profile = ({ user, authentication }) => {
  const { settings, isRetailCircle } = useContext(SettingsContext);
  // react-router sets location.state to null (not undefined) when navigating
  // without state, so a `= {}` destructure default never applies — read it
  // null-safely instead.
  const location = useLocation();
  const isNewAccount = location?.state?.isNewAccount ?? false;
  const [resendStatus, setResendStatus] = useState("initial");
  const [avatar, setAvatar] = useState(user.avatar_file_id);
  const recaptchaRef = useRef();

  const changeAvatar = async (file) => {
    const { data } = await axios.put(`/api/users/${user.id}/avatar`, {
      file,
    });

    authentication.update(data);
  };

  const onResendClick = async (e) => {
    e.preventDefault();

    setResendStatus("submitting");

    try {
      const token = await recaptchaRef.current.executeAsync("change");

      const { data } = await axios.post(`/api/email-verifications`, {
        email: user.email,
        type: "change",
        recaptcha: token,
      });

      setResendStatus("success");
    } catch (err) {
      setResendStatus("error");
    } finally {
      recaptchaRef.current?.reset();
    }
  };

  useEffect(() => {
    if (avatar !== user.avatar_file_id) {
      setAvatar(user.avatar_file_id);

      window.location.reload();
    }
  }, [user.avatar_file_id]);

  const handleIdVerification = async (file) => {
    const { data } = await axios.put(`/api/users/${user.id}/id-upload`, {
      file,
    });

    authentication.update(data);
  };

  return (
    <section id="profile">
      <ReCAPTCHA
        ref={recaptchaRef}
        size="invisible"
        sitekey={RECAPTCHA_SITE_KEY}
      />

      {user.status === "limited_to_referred_product" && (
        <Alert type="warning" message="Account Pending Approval">
          <p>
            Your account is pending verification by {settings.name} staff.{" "}
            <br />
            We will notify you once it is activated.
          </p>
        </Alert>
      )}

      {isRetailCircle && user.approval_status !== 'approved' && (
        <Alert type="warning" message="Account Pending Approval">
          <p>
            Your account must be verified by a Retail Circle admin before you can
            access all features.
            <br />
            You will be notified once your account has been approved.
          </p>
        </Alert>
      )}

      {!isRetailCircle && !user.email_verified_at && (
        <Alert type="error" message="Verify Your Email">
          <p>
            You won't be able to access {settings.name} features until the email
            is verified.
            <br />
            To verify, open the link we emailed to {user.email}.
            {resendStatus === "initial" && (
              <span>
                {" "}
                <a href="#" onClick={onResendClick}>
                  Click here
                </a>{" "}
                to re-send it.
              </span>
            )}
            {resendStatus === "submitting" && (
              <span>
                <Loading size={16} type="small" />
              </span>
            )}
          </p>
        </Alert>
      )}

      {resendStatus === "success" && (
        <Alert
          type="success"
          message="Email sent"
          description="Verification message successfully resent."
        />
      )}

      {resendStatus === "error" && (
        <Alert
          type="error"
          message="Failed to send email"
          description="Failed to resent the verification email."
        />
      )}
      <div className={`row ${isMobile ? "mx-1" : ""}`}>
        {(!user.avatar_file_id || !user.is_avatar_verified) && (
          <VerificationCard
            {...verificationConstant.photo}
            onSave={changeAvatar}
          />
        )}
        {!user.id_file_id && (
          <VerificationCard
            {...verificationConstant.id}
            onSave={handleIdVerification}
          />
        )}
        <div className="col-12">
          <div className="card profile profile-hero">
            <A2SHPopup isNewAccount={isNewAccount} />

            <div className="d-flex flex-wrap">
              <div className="flex-shrink-1 mb-4" style={{ minWidth: "170px" }}>
                <AvatarInput
                  value={user.avatar_file_id}
                  onAvatarChange={changeAvatar}
                  user={user}
                />
              </div>

              <div className="flex-grow-1">
                <BasicInformation user={user} />
              </div>

              <div className="flex-grow-1 mt-4 mt-md-0">
                <SecondaryInformation user={user} />
              </div>
            </div>
          </div>
        </div>
      </div>

      <SlideDown>
        <ProfileEditView user={user} authentication={authentication} />
      </SlideDown>
    </section>
  );
};

export default Profile;
